import React, {useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import SidebarLayout from '../../dashboard/components/SidebarLayout';
import {PermissionDeniedCard} from '../../../Presentation/components/common';
import Card from '../../../Presentation/components/ui/Card';
import {Store} from '../models/store.model';

// Import existing components
import StoreTable from '../components/tables/StoreTable';
import StoreDetailsModal from '../components/modals/StoreDetailsModal';
import {EditStoreModal} from '../components/modals/EditStoreModal';

// Import new refactored components
import Tabs from '../../../Presentation/components/ui/Tabs';
import {Store as StoreIcon, TrendingUp} from 'lucide-react';
import {StoresPageHeader} from '../components/headers/StoresPageHeader';
import {StoresListFilters} from '../components/filters/StoresListFilters';
import {StoresStatisticsTab} from '../components/statistics/StoresStatisticsTab';

// Import hooks
import {useStoresManagement} from '../hooks/useStoresManagement';

import {PAGINATION_CONFIG} from '../../../constants/appConfig';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';

type ActiveTab = 'list' | 'statistics';

const StoresManagementPage: React.FC = () => {
  const {hasPermission} = useAuthorization();

  const {
    // UI State
    activeTab, setActiveTab,
    isDetailsModalOpen, isEditModalOpen,
    selectedStoreId, editingStore, deletingStoreId,
    searchQuery, setSearchQuery,
    paymentStatusFilter, setPaymentStatusFilter,
    cityFilter, districtFilter,
    handleCityChange, handleDistrictChange, handleApplyFilters,
    clearFilters,

    // Data
    stores, statistics, pagination,

    // Loading States
    loading, statisticsLoading,

    // Error States
    error, statisticsError,

    // Actions
    handleDeleteStore, handleViewDetails, handleOpenDetailsByStoreId, handleCloseDetailsModal,
    handleEditStore, handleCloseEditModal, handleEditSuccess,
    handleCreateSuccess, handlePageChange, handlePageSizeChange,
    refreshData
  } = useStoresManagement();

  const location = useLocation();
  const navigate = useNavigate();

  // عند القدوم من الخريطة بزر "عرض تفاصيل المتجر" نفتح مودال التفاصيل تلقائياً
  useEffect(() => {
    const state = location.state as { openStoreId?: number } | null;
    if (state?.openStoreId != null) {
      handleOpenDetailsByStoreId(String(state.openStoreId));
      navigate(location.pathname, { replace: true, state: {} }); // مسح state لتفادي إعادة فتح المودال
    }
  }, [location.state, location.pathname, navigate, handleOpenDetailsByStoreId]);

  // Check if user has permission to view stores
  if (!hasPermission(PERMISSIONS.READ_STORES) && !hasPermission(PERMISSIONS.MANAGE_STORES)) {
    return (
      <SidebarLayout>
        <div className="p-6 min-h-screen rounded-xl bg-[#F2F3F5]">
          <PermissionDeniedCard message="ليس لديك صلاحيات للوصول إلى إدارة المتاجر. يرجى التواصل مع المدير للحصول على الصلاحيات المناسبة." />
        </div>
      </SidebarLayout>
    );
  }

  // All handlers are now provided by the unified hook

  // City/district filtering is applied server-side; the button re-applies them.
  const handleStoreFilter = () => {
    handleApplyFilters();
  };

  const handleTabChange = (tabKey: string) => {
    setActiveTab(tabKey as ActiveTab);
  };

  // Filter stores based on search
  const normalizedSearch = searchQuery.replace(/\s+/g, '').toLowerCase();
  const filteredStores = stores.filter(store =>
    store.name.replace(/\s+/g, '').toLowerCase().includes(normalizedSearch) ||
    store.managerName.replace(/\s+/g, '').toLowerCase().includes(normalizedSearch) ||
    store.phoneNumber.includes(searchQuery) ||
    store.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (paymentStatusFilter && store.paymentStatus === paymentStatusFilter)
  );

  const renderListTab = () => (
    <div className="space-y-6 p-6">
      {/* Search and Filters */}
      <StoresListFilters
        search={searchQuery}
        paymentStatusFilter={paymentStatusFilter}
        cityFilter={cityFilter}
        districtFilter={districtFilter}
        onSearchChange={setSearchQuery}
        onPaymentStatusChange={setPaymentStatusFilter}
        onCityChange={handleCityChange}
        onDistrictChange={handleDistrictChange}
        onFilter={handleStoreFilter}
      />

      {/* Stores Table */}
      <StoreTable
        data={filteredStores}
        page={pagination?.page || 1}
        pageSize={pagination?.limit || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE}
        totalCount={pagination?.total || stores?.length || 0}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onDeleteStore={handleDeleteStore}
        deletingStoreId={deletingStoreId}
        onViewDetails={handleViewDetails}
        showPageSizeSelector={true}
        showPaginationInfo={true}
        exportable={true}
        loading={loading}
      />
    </div>
  );

  const visibleTabs = [
    hasPermission(PERMISSIONS.MANAGE_STORES) && {
      key: 'list' as const,
      label: 'قائمة المتاجر',
      description: 'عرض وإدارة المتاجر',
      icon: <StoreIcon className="w-5 h-5" />,
      badge: stores?.length ?? pagination?.total ?? 0,
      content: renderListTab()
    },
    hasPermission(PERMISSIONS.VIEW_DASHBOARD) && {
      key: 'statistics' as const,
      label: 'الإحصائيات',
      description: 'إحصائيات المتاجر',
      icon: <TrendingUp className="w-5 h-5" />,
      content: (
        <div className="p-6">
        <StoresStatisticsTab
          statistics={statistics}
          statisticsLoading={statisticsLoading}
          statisticsError={statisticsError}
        />
        </div>
      )
    }
  ].filter(Boolean) as {key: ActiveTab; label: string; description: string; icon: React.ReactNode; badge?: number; content: React.ReactNode}[];

  return (
    <SidebarLayout>
      <div className="p-6 space-y-6">
        {/* Page Header */}
        <StoresPageHeader />

        {/* Error is handled by StandardizedErrorHandler and shown as toast notification */}

        <Card className="p-0 overflow-hidden shadow-sm">
          <Tabs
            items={visibleTabs}
            defaultActiveKey={activeTab}
            onChange={handleTabChange}
            variant="modern"
            className="flex-1"
          />
        </Card>

        {/* Store Details Modal */}
        <StoreDetailsModal
          storeId={selectedStoreId}
          isOpen={isDetailsModalOpen}
          onClose={handleCloseDetailsModal}
          onEdit={handleEditStore}
          onDelete={handleDeleteStore}
          stores={stores}
        />

        {/* Edit Store Modal */}
        <EditStoreModal
          store={editingStore}
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          onSuccess={handleEditSuccess}
        />
      </div>
    </SidebarLayout>
  );
};

export default StoresManagementPage;
